/**
 * Shopper AI (Support) specific keyboard shortcuts and scopes.
 * This scope tree is nested under "shopperAi" in the global registry.
 */

import type { KeyboardShortcut } from "./keyboard-shortcut";

/**
 * Shopper AI scope tree.
 * These scopes are nested under the shopperAi root scope in the global registry.
 */
export const shopperAiScopes = [
  /** actionMenu scope - main action shortcuts */
  "actionMenu",
  /** Support ticket table navigation - j/k to navigate, Enter to open */
  "supportTicketsTable",
  /** Priority selection submenu */
  "priority",
  /** Assignee selection submenu */
  "assign",
  /** Tag selection submenu */
  "addTag",
  /** Snooze time selection submenu */
  "snooze",
  /** Message input - send and draft management */
  "messageInput",
  /** Support ticket detail view - navigate between support tickets */
  "supportTicketDetail",
] as const;

export type ShopperAiScope = (typeof shopperAiScopes)[number];

/** Shopper AI specific keyboard shortcut with scope */
export type ShopperAiShortcut = KeyboardShortcut<ShopperAiScope>;

/**
 * Support ticket action keyboard shortcuts.
 * actionMenu-level actions use simple keys (activated when menu is open).
 */
export const supportTicketActionShortcuts = {
  CLOSE: { code: "KeyC", keyLabel: "C", scope: "actionMenu" },
  REOPEN: { code: "KeyO", keyLabel: "O", scope: "actionMenu" },
  SNOOZE: { code: "KeyS", keyLabel: "S", scope: "actionMenu" },
  ASSIGN: { code: "KeyA", keyLabel: "A", scope: "actionMenu" },
  MARK_READ: { code: "KeyR", keyLabel: "R", scope: "actionMenu" },
  MARK_UNREAD: { code: "KeyU", keyLabel: "U", scope: "actionMenu" },
  SET_PRIORITY: { code: "KeyP", keyLabel: "P", scope: "actionMenu" },
  ADD_TAG: { code: "KeyT", keyLabel: "T", scope: "actionMenu" },
  MARK_IN_PROGRESS: { code: "KeyI", keyLabel: "I", scope: "actionMenu" },
  ARCHIVE: { code: "KeyE", keyLabel: "E", scope: "actionMenu" },
  DELETE: { code: "KeyD", keyLabel: "D", scope: "actionMenu" },
  MERGE: { code: "KeyM", keyLabel: "M", scope: "actionMenu" },
} as const satisfies Record<string, ShopperAiShortcut>;

export const priorityShortcuts = {
  PRIORITY_URGENT: { code: "KeyU", keyLabel: "U", scope: "priority" },
  PRIORITY_CLEAR: { code: "KeyX", keyLabel: "X", scope: "priority" },
} as const satisfies Record<string, ShopperAiShortcut>;

export const snoozeShortcuts = {
  SNOOZE_1H: { code: "Digit1", keyLabel: "1", scope: "snooze" },
  SNOOZE_4H: { code: "Digit2", keyLabel: "2", scope: "snooze" },
  SNOOZE_24H: { code: "Digit3", keyLabel: "3", scope: "snooze" },
  SNOOZE_CUSTOM: { code: "Digit4", keyLabel: "4", scope: "snooze" },
  CLEAR_SNOOZE: { code: "KeyX", keyLabel: "X", scope: "snooze" },
} as const satisfies Record<string, ShopperAiShortcut>;

/**
 * Table navigation shortcuts for the support ticket list.
 * Vim-style navigation keys without modifiers.
 */
export const tableNavigationShortcuts = {
  NAVIGATE_DOWN: { code: "KeyJ", keyLabel: "J", scope: "supportTicketsTable" },
  NAVIGATE_UP: { code: "KeyK", keyLabel: "K", scope: "supportTicketsTable" },
  OPEN_SUPPORT_TICKET: {
    code: "Enter",
    keyLabel: "Enter",
    scope: "supportTicketsTable",
  },
  OPEN_ACTION_MENU: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["alt"],
    scope: "supportTicketsTable",
  },
} as const satisfies Record<string, ShopperAiShortcut>;

export const messageInputShortcuts = {
  SEND_MESSAGE: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["meta"],
    scope: "messageInput",
    allowInEditor: true,
  },
  SEND_AND_CLOSE: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["meta", "shift"],
    scope: "messageInput",
    allowInEditor: true,
  },
  SEND_AND_MARK_IN_PROGRESS: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["alt", "shift"],
    scope: "messageInput",
    allowInEditor: true,
  },
  SEND_AND_SNOOZE: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["meta", "alt"],
    scope: "messageInput",
    allowInEditor: true,
  },
  AI_SUGGEST: {
    code: "Digit1",
    keyLabel: "1",
    modifiers: ["alt"],
    scope: "messageInput",
    allowInEditor: true,
  },
  AI_POLISH: {
    code: "Digit2",
    keyLabel: "2",
    modifiers: ["alt"],
    scope: "messageInput",
    allowInEditor: true,
  },
  INSERT_MENTION: {
    code: "Digit2",
    keyLabel: "2",
    modifiers: ["meta"],
    scope: "messageInput",
    allowInEditor: true,
  },
  INSERT_DISCOUNT_CODE: {
    code: "Digit5",
    keyLabel: "5",
    modifiers: ["meta"],
    scope: "messageInput",
    allowInEditor: true,
  },
  OPEN_MACRO_SELECTOR: {
    code: "Digit6",
    keyLabel: "6",
    modifiers: ["meta"],
    scope: "messageInput",
    allowInEditor: true,
  },
} as const satisfies Record<string, ShopperAiShortcut>;

export const supportTicketDetailShortcuts = {
  NAVIGATE_UP: { code: "ArrowUp", keyLabel: "↑", scope: "supportTicketDetail" },
  NAVIGATE_DOWN: {
    code: "ArrowDown",
    keyLabel: "↓",
    scope: "supportTicketDetail",
  },
  REOPEN: {
    code: "KeyR",
    keyLabel: "R",
    modifiers: ["alt"],
    scope: "supportTicketDetail",
  },
  MARK_IN_PROGRESS: {
    code: "KeyP",
    keyLabel: "P",
    modifiers: ["alt"],
    scope: "supportTicketDetail",
  },
  NAVIGATE_PREV: {
    code: "KeyJ",
    keyLabel: "J",
    modifiers: ["alt"],
    scope: "supportTicketDetail",
  },
  NAVIGATE_NEXT: {
    code: "KeyK",
    keyLabel: "K",
    modifiers: ["alt"],
    scope: "supportTicketDetail",
  },
  OPEN_ACTION_MENU: {
    code: "Enter",
    keyLabel: "Enter",
    modifiers: ["alt"],
    scope: "supportTicketDetail",
  },
} as const satisfies Record<string, ShopperAiShortcut>;

export type SupportTicketActionShortcutId =
  keyof typeof supportTicketActionShortcuts;

export type TableNavigationShortcutId = keyof typeof tableNavigationShortcuts;
